import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Loader2, PoundSterling, Clock, CheckCircle, 
  FileText, Building, AlertTriangle, CreditCard, Banknote,
  Upload, Receipt, Users, TrendingUp
} from "lucide-react";
import { API } from "@/App";

export const PaymentTrackingPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({});
  const [activeTab, setActiveTab] = useState("all");
  
  // Mark paid modal
  const [markPaidModal, setMarkPaidModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [paymentType, setPaymentType] = useState("contractor"); // contractor or landlord
  const [paymentReference, setPaymentReference] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment-tracking`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { filter_type: activeTab }
      });
      setJobs(response.data.jobs || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error("Error fetching payment data:", error);
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  const openMarkPaidModal = (job, type) => {
    setSelectedJob(job);
    setPaymentType(type);
    setPaymentReference("");
    setMarkPaidModal(true);
  };

  const handleMarkPaid = async () => {
    if (!selectedJob) return;
    
    setProcessing(true);
    try {
      const endpoint = paymentType === "contractor" 
        ? `${API}/admin/mark-contractor-paid/${selectedJob.job_id}`
        : `${API}/admin/mark-landlord-paid/${selectedJob.job_id}`;
      
      await axios.post(endpoint, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { payment_reference: paymentReference || undefined }
      });
      
      toast.success(`${paymentType === "contractor" ? "Contractor" : "Landlord"} payment marked as paid`);
      setMarkPaidModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getContractorStatusBadge = (job) => {
    if (job.contractor_payment_status === "paid") {
      return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
    }
    if (job.contractor_payment_status === "invoiced") {
      return <Badge className="bg-amber-100 text-amber-700">Invoiced</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700">Awaiting Invoice</Badge>;
  };

  const getLandlordStatusBadge = (job) => {
    if (job.landlord_payment_status === "paid") {
      return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
    }
    if (job.payment_method === "deduct_from_rent") {
      return <Badge className="bg-blue-100 text-blue-700">Deduct from Rent</Badge>;
    }
    return <Badge className="bg-red-100 text-red-700">Unpaid</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold font-outfit">Payment Tracking</h1>
                <p className="text-sm text-muted-foreground">Manage contractor & landlord payments</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{summary.total_completed}</p>
                  <p className="text-sm text-green-600">Completed Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{summary.contractor_invoiced}</p>
                  <p className="text-sm text-amber-600">Contractor Invoiced</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <PoundSterling className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-700">£{(summary.total_owed_to_contractors || 0).toFixed(0)}</p>
                  <p className="text-sm text-red-600">Owed to Contractors</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Banknote className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">£{(summary.total_owed_by_landlords || 0).toFixed(0)}</p>
                  <p className="text-sm text-blue-600">Owed by Landlords</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="flex-wrap">
            <TabsTrigger value="all">
              All ({summary.total_completed})
            </TabsTrigger>
            <TabsTrigger value="contractor_awaiting_invoice">
              <Clock className="w-4 h-4 mr-1" />
              Awaiting Invoice ({summary.contractor_awaiting_invoice})
            </TabsTrigger>
            <TabsTrigger value="contractor_invoiced">
              <Receipt className="w-4 h-4 mr-1" />
              Contractor Invoiced ({summary.contractor_invoiced})
            </TabsTrigger>
            <TabsTrigger value="landlord_unpaid">
              <AlertTriangle className="w-4 h-4 mr-1" />
              Landlord Unpaid ({summary.landlord_unpaid})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-4">
            {jobs.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <PoundSterling className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">No jobs found for this filter</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {jobs.map((job) => (
                  <Card key={job.job_id} data-testid={`payment-job-${job.job_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="font-mono">
                              #{job.job_id?.slice(-8).toUpperCase()}
                            </Badge>
                            <span className="font-medium">{job.issue_type}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            Contractor: {job.contractor_name || "Unassigned"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Completed: {formatDate(job.completed_at)}
                          </p>
                        </div>

                        {/* Contractor Payment Section */}
                        <div className="bg-slate-50 rounded-lg p-3 min-w-[200px]">
                          <p className="text-xs text-muted-foreground mb-1">Contractor Payment</p>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">
                              £{(job.contractor_invoice_amount || 0).toFixed(2)}
                            </span>
                            {getContractorStatusBadge(job)}
                          </div>
                          {job.contractor_invoice_date && (
                            <p className="text-xs text-muted-foreground">
                              Invoiced: {formatDate(job.contractor_invoice_date)}
                            </p>
                          )}
                          {job.contractor_payment_status === "invoiced" && (
                            <Button 
                              size="sm" 
                              className="w-full mt-2"
                              onClick={() => openMarkPaidModal(job, "contractor")}
                              data-testid={`mark-contractor-paid-${job.job_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Mark Paid
                            </Button>
                          )}
                        </div>

                        {/* Landlord Payment Section */}
                        <div className="bg-slate-50 rounded-lg p-3 min-w-[200px]">
                          <p className="text-xs text-muted-foreground mb-1">Landlord Payment</p>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">
                              £{(job.quote_amount || 0).toFixed(2)}
                            </span>
                            {getLandlordStatusBadge(job)}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Method: {job.payment_method === "deduct_from_rent" ? "Deduct from Rent" : "Pay Direct"}
                          </p>
                          {job.landlord_payment_status !== "paid" && job.payment_method !== "deduct_from_rent" && (
                            <Button 
                              size="sm" 
                              className="w-full mt-2"
                              onClick={() => openMarkPaidModal(job, "landlord")}
                              data-testid={`mark-landlord-paid-${job.job_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Mark Paid
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Mark Paid Modal */}
      <Dialog open={markPaidModal} onOpenChange={setMarkPaidModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Mark {paymentType === "contractor" ? "Contractor" : "Landlord"} as Paid
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Job Reference</p>
              <p className="font-semibold">#{selectedJob?.job_id?.slice(-8).toUpperCase()}</p>
              <p className="text-sm mt-2">
                Amount: <span className="font-semibold text-primary">
                  £{paymentType === "contractor" 
                    ? (selectedJob?.contractor_invoice_amount || 0).toFixed(2)
                    : (selectedJob?.quote_amount || 0).toFixed(2)
                  }
                </span>
              </p>
            </div>

            <div className="space-y-2">
              <Label>Payment Reference (optional)</Label>
              <Input
                placeholder="e.g., Bank transfer ref, cheque number..."
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleMarkPaid} disabled={processing}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
